import cn from 'classnames';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Icon, Spinner } from 'components';
import { ButtonV3, InputV2 } from 'components/ComponentV2';
import { Button } from 'components/form';

import { API_ROUTES } from 'lib/api-routes';
import { CustomLinksAttributesWithSelection } from 'lib/atoms/signUpSocietyAtom';
import { ONBOARDING_FLOW_STATE } from 'lib/consts/flowState';
import { StudentSocietyResponse } from 'lib/models/student-society';
import { TUserApiResponse } from 'lib/models/User.model';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { apiInstance } from 'lib/utils/axios';
import { parseArrayResponse, parseErrorResponse } from 'lib/utils/parser';
import { REGEX } from 'lib/utils/regex';

import Footer from '../Footer';
import Header from '../Header';
import StepHeading from '../StepHeading';
import AddOrEditLinkModal from './AddOrEditLinkModal';
import LinkCard from './CustomLinkCard';
import styles from './Step2.module.scss';

interface Step2Props {
  progress: number;
  setFlowState: (flowState: string) => void;
  customLink?: CustomLinksAttributesWithSelection[];
  setCustomLink: (
    customLink: CustomLinksAttributesWithSelection[] | undefined
  ) => void;
}

export const Step2: React.FC<Step2Props> = ({
  progress,
  setFlowState,
  customLink,
  setCustomLink,
}) => {
  const { managerSociety, updateUserApiResponse, manager } = useAuth();

  const [t] = useTranslation('onboarding.manager');

  const [isCompleted, setIsCompleted] = React.useState(false);

  const [isEventExist, setIsEventExist] = React.useState(false);
  //handle this link tree url and custom links with selected attribute
  const { linktree_url } = manager || { linktree_url: '' };

  useEffect(() => {
    fetchLinkTree();
  }, []);

  const [linkTreeResultSuccessful, setLinkTreeResultSuccessful] =
    React.useState<boolean | undefined>();

  const [noOfLinkTreeLink, setNoOfLinkTreeLink] = React.useState<number>(0);

  const [linkTreeFetchError, setLinkTreeFetchError] =
    React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalEditIndex, setModalEditIndex] = useState<number>();
  const [addLinkManually, setAddLinkManually] = useState<boolean>(
    customLink?.length ? true : false
  );

  useEffect(() => {
    setAddLinkManually(customLink?.length ? true : false);
  }, [customLink]);

  const [showError, setShowError] = React.useState<string>('');
  const [slug, setSlug] = useState<string | undefined>();

  useEffect(() => {
    if (managerSociety === null) return;
    const {
      instagram_url,
      facebook_url,
      linkedin_url,
      website,
      student_union_url,
      short_description,
      description,
      banner_url,
      custom_links,
      primary_topic,
      managers,
      stats,
      offerings,
      student_societies_highlights,
      flagship_events,
      slug,
    } = managerSociety;
    setSlug(slug);
    setIsCompleted(
      !!banner_url &&
        !!description &&
        !!short_description &&
        !!custom_links?.data.length &&
        !!(
          instagram_url ||
          facebook_url ||
          linkedin_url ||
          website ||
          student_union_url
        ) &&
        !!primary_topic &&
        !!stats?.data.length &&
        !!managers?.data.length &&
        !!offerings?.data.length &&
        !!student_societies_highlights?.data.length
    );

    setIsEventExist(!!flagship_events?.data.length);
  }, [managerSociety]);

  const handleNext = async () => {
    const flow_state = isCompleted
      ? isEventExist
        ? ONBOARDING_FLOW_STATE.COMPLETE
        : ONBOARDING_FLOW_STATE.POST_4
      : ONBOARDING_FLOW_STATE.POST_3;
    const manager = { flow_state };
    const { data: response } = await apiInstance.patch<TUserApiResponse>(
      API_ROUTES.MANAGER_USER,
      {
        manager,
      }
    );
    updateUserApiResponse(response);
    setFlowState(flow_state);
  };

  const fetchLinkTree = async () => {
    const linkTreeUrl = getValues('linktree_url');
    if (linkTreeUrl === '') return;

    if (!REGEX.URL.test(linkTreeUrl)) {
      setError('linktree_url', { message: t('invalid-url') });
      return;
    }
    setIsLoading(true);
    try {
      const { data: response } = await apiInstance.post<
        { url: string; title: string }[]
      >(API_ROUTES.FETCH_LINK_TREE, { linktree_url: linkTreeUrl });

      if (response.length) {
        setNoOfLinkTreeLink(response.length);
        setLinkTreeFetchError('');
        setLinkTreeResultSuccessful(true);

        const alreadyAddedLinks: CustomLinksAttributesWithSelection[] =
          parseArrayResponse(managerSociety?.custom_links).map((linkObj) => {
            return { ...linkObj, selected: true };
          });

        const fetchedLink = response
          .map((link) => {
            if (
              !alreadyAddedLinks.some((linkObj) => linkObj.link === link.url)
            ) {
              return {
                link: link.url,
                title: link.title,
                selected: false,
              } as CustomLinksAttributesWithSelection;
            }
            return null; // Return null instead of undefined
          })
          .filter(
            (link): link is CustomLinksAttributesWithSelection => link !== null
          );

        setCustomLink([...alreadyAddedLinks, ...fetchedLink]);

        const manager = { linktree_url: linkTreeUrl };
        const { data: managerResponse } =
          await apiInstance.patch<TUserApiResponse>(API_ROUTES.MANAGER_USER, {
            manager,
          });
        updateUserApiResponse(managerResponse);
      } else {
        handleLinkTreeFail();
      }
    } catch (error) {
      console.error(error);
      handleLinkTreeFail();
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkTreeFail = () => {
    setLinkTreeFetchError(t('step-2.link-tree.fetch-error'));
    setLinkTreeResultSuccessful(false);
    setCustomLink(undefined);
    setNoOfLinkTreeLink(0);
  };

  const saveLink = (title: string, link: string, index?: number) => {
    if (index !== undefined && customLink) {
      const newCustomLink = [...customLink];
      newCustomLink[index] = {
        ...customLink[index],
        title,
        link,
      };
      setCustomLink(newCustomLink);
    } else {
      setCustomLink([
        ...(customLink || []),
        {
          title,
          link,
          selected: true,
        } as CustomLinksAttributesWithSelection,
      ]);
    }
  };

  const deleteLink = (index: number) => {
    if (customLink) {
      const newCustomLink = [...customLink];
      newCustomLink[index] = {
        ...customLink[index],
        selected: true,
        _destroy: true,
      };

      setCustomLink(newCustomLink);
    }

    setIsModalOpen(false);
    setModalEditIndex(undefined);
  };

  const changeEmojiAndSelected = (
    index: number,
    selected: boolean,
    emoji?: string
  ) => {
    if (customLink) {
      const newCustomLink = [...customLink];
      newCustomLink[index] = {
        ...customLink[index],
        emoji,
        selected,
      } as CustomLinksAttributesWithSelection;
      setCustomLink(newCustomLink);
    }
  };

  const {
    register,
    formState: { errors, isSubmitting },
    getValues,
    setError,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      linktree_url: linktree_url || '',
    },
  });

  useEffect(() => {
    const subscription = watch((value) => {
      const { linktree_url } = value;
      if (linktree_url !== undefined) {
        setError('linktree_url', {});
        setLinkTreeFetchError('');
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleSubmit = async () => {
    const linksToDelete =
      customLink
        ?.filter((link) => !link.selected && link.id)
        .map((link) => ({ ...link, _destroy: true })) || [];
    const linkToUpdate = customLink?.filter((link) => link.selected) || [];

    const body = {
      student_society: {
        custom_links_attributes: [...linkToUpdate, ...linksToDelete],
      },
    };

    try {
      await apiInstance.patch<StudentSocietyResponse>(
        API_ROUTES.STUDENT_SOCIETY,
        body
      );
      await handleNext();
    } catch (error) {
      const message = parseErrorResponse(error as Error);
      setShowError(message);
    }
  };

  return (
    <>
      <Header
        percentComplete={progress}
        onBack={() => {
          if (addLinkManually) {
            setAddLinkManually(false);
            setLinkTreeResultSuccessful(undefined);
            setLinkTreeFetchError('');
            setNoOfLinkTreeLink(0);
            setCustomLink(undefined);
            setValue('linktree_url', '');
            setFlowState(ONBOARDING_FLOW_STATE.POST_2);
          } else {
            setFlowState(ONBOARDING_FLOW_STATE.POST_1);
          }
        }}
      />
      <div className={styles.stepContent}>
        <StepHeading
          title={
            addLinkManually
              ? t('step-2.header.title-add-manually')
              : t('step-2.header.title')
          }
          desc={
            addLinkManually
              ? t('step-2.header.desc-add-manually')
              : t('step-2.header.desc')
          }
        />

        {!addLinkManually && (
          <>
            <InputV2
              {...register('linktree_url', {
                pattern: {
                  value: REGEX.URL,
                  message: t('invalid-url'),
                },
              })}
              labelClassName={styles.fieldLabel}
              placeholder={t('step-2.link-tree.placeholder')}
              isInvalid={
                (!!errors.linktree_url?.message || !!linkTreeFetchError) &&
                !isLoading
              }
              showEnterText={
                linkTreeResultSuccessful === undefined && !isLoading
                  ? 'import'
                  : undefined
              }
              onKeyDown={async (event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  fetchLinkTree();
                }
              }}
              className={cn({
                [styles.successInput]: !isLoading && linkTreeResultSuccessful,
              })}
              rightIcon={
                isLoading ? (
                  <Spinner size="xsmall" />
                ) : (
                  linkTreeResultSuccessful && (
                    <Icon
                      iconName={'icon_check-double'}
                      className={styles.linkTreeSuccessIcon}
                    />
                  )
                )
              }
              disabled={isLoading}
            />
            {errors.linktree_url?.message && (
              <div className={styles.errorMessage}>
                {errors.linktree_url?.message}
              </div>
            )}
            {linkTreeFetchError && (
              <div className={styles.errorMessage}>{linkTreeFetchError}</div>
            )}
          </>
        )}

        {linkTreeResultSuccessful === true ? (
          <>
            <div className={styles.sectionSeparator}></div>
            <div className={styles.linkTreeSuccessText}>
              {t('step-2.link-tree.success-text', { noOfLinkTreeLink })}
            </div>
          </>
        ) : null}

        {isModalOpen && (
          <AddOrEditLinkModal
            saveLink={saveLink}
            isEdit={modalEditIndex !== undefined}
            onClose={() => {
              setIsModalOpen(false);
              setModalEditIndex(undefined);
            }}
            index={modalEditIndex}
            deleteLink={deleteLink}
            title={
              modalEditIndex !== undefined
                ? customLink?.[modalEditIndex]?.title
                : undefined
            }
            link={
              modalEditIndex !== undefined
                ? customLink?.[modalEditIndex]?.link
                : undefined
            }
          />
        )}
        {customLink?.length ? (
          <div className={styles.customLinkContainer}>
            {customLink.map((link, index) => (
              <>
                {link._destroy ? (
                  <></>
                ) : (
                  <LinkCard
                    key={index}
                    link={link}
                    index={index}
                    onCardClick={() => {
                      setModalEditIndex(index);
                      setIsModalOpen(true);
                    }}
                    changeLinkData={changeEmojiAndSelected}
                  />
                )}
              </>
            ))}
          </div>
        ) : null}
        {linkTreeResultSuccessful !== undefined &&
          (customLink?.length ? (
            <Button
              type="button"
              color="secondary"
              className={cn(
                styles.whiteButton,
                styles.mt16,
                styles.addMoreLink
              )}
              onClick={() => {
                setIsModalOpen(true);
              }}
            >
              {t('step-2.add-link')}
            </Button>
          ) : (
            <Button
              type="button"
              color="secondary"
              className={cn(styles.whiteButton, styles.mt42)}
              onClick={() => {
                setIsModalOpen(true);
                setAddLinkManually(true);
              }}
              isFullWidth
            >
              {t('step-2.add-link-manually')}
            </Button>
          ))}
        {showError && <div className={styles.errorMessage}>{showError}</div>}
        {showError && <div className={styles.errorMessage}>{showError}</div>}

        <Footer>
          {linkTreeResultSuccessful !== true && !addLinkManually ? (
            <Button
              type="button"
              color="secondary"
              className={styles.whiteButton}
              onClick={() => handleNext()}
              isFullWidth
            >
              {t('step-2.add-links-later')}
            </Button>
          ) : (
            <>
              <Button
                type="button"
                color="secondary"
                className={styles.whiteButton}
                onClick={() => {
                  const win = window.open(
                    `${PAGE_ROUTES.SOCIETIES}/${slug}`,
                    '_blank'
                  );
                  win?.focus();
                }}
                isFullWidth
              >
                {t('step-2.preview-profile')}
              </Button>
              <ButtonV3
                type="submit"
                isFullWidth
                isLoading={isSubmitting}
                size="xlarge"
                onClick={handleSubmit}
              >
                {t('next')}
              </ButtonV3>
            </>
          )}
        </Footer>
      </div>
    </>
  );
};
